import React, { useState } from 'react';
import { Box, Checkbox, Divider, FormControlLabel, Grid, Typography } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Formik, Form } from 'formik';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setApplicantConsent, setUserConsent } from '../../redux-store/actions/onBoarding';
import {
  ApplicationProps,
  OcrNomineeDetail,
  OcrResponseBody,
} from '../../redux-store/types/api-types';
import { MFTextField } from '../../lib/formik';
import MFSelectField from '../../lib/formik/SelectField';
import { DatePicker } from '../../lib/formik/DatePicker';
import { formatShortDate } from '../../utils/date';
import { reviewFetchedDetailsSchema } from '../../utils/schema';
import {
  newGrossAnnualMaster,
  BankAccountTypeMaster,
  NRIBankAccountTypeMaster,
  occupationDetailsMasters,
  DLCLMasters,
} from '../../utils/constant';

// ─── Types ───────────────────────────────────────────────────────────────────

export type ApplicantConsentState = {
  mode: 'applicantConsent';
  applicantId: string;
  pan?: string;
  dob?: string;
  mobile?: string;
  email?: string;
  application?: ApplicationProps;
  firstHolderName?: string;
};

type FullReviewState = OcrResponseBody & { mode?: 'full' };
type ReviewLocationState = FullReviewState | ApplicantConsentState;

type ReviewFormValues = {
  bankDetails: OcrResponseBody['bankDetails'];
  applicantDetails: OcrResponseBody['applicantDetails'];
  nomineeDetails: OcrNomineeDetail[];
  secondHolderDetails: OcrResponseBody['secondHolderDetails'];
};

// ─── Select options ───────────────────────────────────────────────────────────

const ANNUAL_INCOME_ITEMS = newGrossAnnualMaster.map((v) => ({ key: v, value: v }));

const ACCOUNT_TYPE_ITEMS = [
  ...Object.entries(BankAccountTypeMaster).map(([k, v]) => ({ key: k, value: v })),
  ...Object.entries(NRIBankAccountTypeMaster).map(([k, v]) => ({ key: k, value: v })),
];

const OCCUPATION_ITEMS = occupationDetailsMasters.map((v) => ({ key: v, value: v }));

const DLCL_ITEMS = Object.keys(DLCLMasters).map((id) => ({
  key: DLCLMasters[id],
  value: id,
}));

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Apply a transform to every value in a plain object — coerces all values to string first */
function mapSection<T extends Record<string, unknown>>(
  obj: T,
  transform: (v: string) => string | null
): Record<string, string | null> {
  return Object.fromEntries(
    Object.entries(obj).map(([k, v]) => {
      const coerced = v == null ? '' : String(v);
      return [k, transform(coerced)];
    })
  );
}

function buildInitialValues(ocr: OcrResponseBody): ReviewFormValues {
  const toStr = (v: string) => v; // identity — null → ''
  return {
    bankDetails: mapSection(ocr.bankDetails || {}, toStr) as OcrResponseBody['bankDetails'],
    applicantDetails: mapSection(
      ocr.applicantDetails || {},
      toStr
    ) as OcrResponseBody['applicantDetails'],
    nomineeDetails: (ocr.nomineeDetails || []).map((n) => mapSection(n, toStr) as OcrNomineeDetail),
    secondHolderDetails: mapSection(
      ocr.secondHolderDetails || {},
      toStr
    ) as OcrResponseBody['secondHolderDetails'],
  };
}

function toApiShape(id: string, values: ReviewFormValues): OcrResponseBody {
  const nullify = (v: string): string | null => (v.trim() === '' ? null : v.trim());
  return {
    id,
    bankDetails: mapSection(values.bankDetails, nullify) as OcrResponseBody['bankDetails'],
    applicantDetails: mapSection(
      values.applicantDetails,
      nullify
    ) as OcrResponseBody['applicantDetails'],
    nomineeDetails: values.nomineeDetails.map((n) => mapSection(n, nullify) as OcrNomineeDetail),
    secondHolderDetails: mapSection(
      values.secondHolderDetails,
      nullify
    ) as OcrResponseBody['secondHolderDetails'],
  };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function SectionHeading({ title }: { title: string }) {
  return (
    <Typography
      sx={{
        fontSize: 13,
        fontWeight: 600,
        color: 'primary.main',
        mt: 2,
        mb: 1,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      }}>
      {title}
    </Typography>
  );
}

function ReadOnlyField({ label, value }: { label: string; value?: string | null }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 0.5, fontWeight: 500 }}>
        {label}
      </Typography>
      <Box
        sx={{
          backgroundColor: '#f5f5f5',
          borderRadius: '4px',
          px: 2,
          py: 1.25,
          minHeight: 44,
          display: 'flex',
          alignItems: 'center',
        }}>
        <Typography sx={{ fontSize: 14, color: value ? 'text.primary' : 'text.disabled' }}>
          {value || '—'}
        </Typography>
      </Box>
    </Box>
  );
}

function ConsentCheckbox({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <Box
      sx={{
        mt: 1,
        p: 2,
        backgroundColor: '#f9f9f9',
        borderRadius: '8px',
        border: '1px solid',
        borderColor: 'grey.200',
      }}>
      <FormControlLabel
        control={
          <Checkbox
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            color="primary"
            size="small"
          />
        }
        label={
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary' }}>
              Application Consent
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.25 }}>
              I provide consent to the use of these details for completing the investor onboarding
              process fetched from KRA and Document Uploaded.
            </Typography>
          </Box>
        }
        sx={{ alignItems: 'flex-start', '.MuiCheckbox-root': { pt: 0 } }}
      />
    </Box>
  );
}

function ActionButtons({
  isSubmitting,
  consentGiven,
  onCancel,
  onProceed,
}: {
  isSubmitting: boolean;
  consentGiven: boolean;
  onCancel: () => void;
  onProceed?: () => void;
}) {
  return (
    <Grid container sx={{ mt: 2, justifyContent: 'center', gap: 2 }}>
      <LoadingButton
        variant="outlined"
        disabled={isSubmitting}
        onClick={onCancel}
        sx={{ minWidth: 160, fontSize: 14, fontWeight: 500, color: 'primary.main' }}>
        Cancel
      </LoadingButton>
      <LoadingButton
        variant="contained"
        loading={isSubmitting}
        disabled={!consentGiven}
        onClick={onProceed}
        type={onProceed ? 'button' : 'submit'}
        sx={{ minWidth: 160, fontSize: 14, fontWeight: 500, color: 'common.white' }}>
        Proceed
      </LoadingButton>
    </Grid>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ReviewFetchedDetails(): JSX.Element {
  const history = useHistory();
  const location = useLocation<ReviewLocationState>();
  const dispatch = useDispatch();
  const state = location.state || ({} as ReviewLocationState);

  const [consentGiven, setConsentGiven] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isApplicantConsentMode = state.mode === 'applicantConsent';
  const applicantConsentData = isApplicantConsentMode ? (state as ApplicantConsentState) : null;
  const ocrData = isApplicantConsentMode ? null : (state as FullReviewState);

  const initialValues = ocrData ? buildInitialValues(ocrData) : null;

  const handleCancel = async () => {
    if (isApplicantConsentMode && applicantConsentData) {
      try {
        setIsSubmitting(true);
        await (dispatch(
          setApplicantConsent(applicantConsentData.applicantId, { consent: false })
        ) as unknown as Promise<void>);
      } catch (e) {
        console.error((e as Error).message);
      } finally {
        setIsSubmitting(false);
        history.goBack();
      }
    } else {
      history.goBack();
    }
  };

  const handleApplicantConsentProceed = async () => {
    if (!applicantConsentData) return;
    try {
      setIsSubmitting(true);
      await (dispatch(
        setApplicantConsent(applicantConsentData.applicantId, { consent: true })
      ) as unknown as Promise<void>);
      history.push('/onboarding/enter-pan-number', {
        prefillPan: applicantConsentData.pan,
        prefillDob: applicantConsentData.dob,
        prefillMobile: applicantConsentData.mobile,
        prefillEmail: applicantConsentData.email,
        redirectTo: '/application/contributor-details',
        application: applicantConsentData.application,
        firstHolderName: applicantConsentData.firstHolderName,
      });
    } catch (e) {
      console.error((e as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOcrProceed = async (formValues: ReviewFormValues) => {
    if (!ocrData) return;
    try {
      setIsSubmitting(true);
      if (ocrData.id) {
        await (dispatch(
          setUserConsent({
            consent: true,
            id: ocrData.id,
            dataWithConsent: toApiShape(ocrData.id, formValues),
          } as any)
        ) as unknown as Promise<void>);
      }
      history.push('/onboarding/enter-pan-number', {
        prefillPan: formValues.applicantDetails.panNo,
        prefillDob: formValues.applicantDetails.dateOfBirth,
        cmlId: ocrData.id,
        prefillMobile: formValues.applicantDetails.mobile,
        prefillEmail: formValues.applicantDetails.email,
      });
    } catch (e) {
      console.error((e as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const pageTitle = (
    <Typography sx={{ fontSize: 18, fontWeight: 600, color: 'primary.main', mb: 3 }}>
      Review Your Fetched Details
    </Typography>
  );

  // ── Applicant consent mode — read-only ────────────────────────────────────
  if (isApplicantConsentMode && applicantConsentData) {
    return (
      <Box>
        {pageTitle}
        <SectionHeading title="Second Holder Details" />
        <Grid container spacing={2}>
          {[
            { label: 'PAN', value: applicantConsentData.pan },
            { label: 'Date of Birth', value: formatShortDate(applicantConsentData.dob) },
            { label: 'Mobile Number', value: applicantConsentData.mobile },
            { label: 'Email ID', value: applicantConsentData.email },
          ].map(({ label, value }) => (
            <Grid item xs={12} sm={6} key={label}>
              <ReadOnlyField label={label} value={value} />
            </Grid>
          ))}
        </Grid>
        <ConsentCheckbox checked={consentGiven} onChange={setConsentGiven} />
        <ActionButtons
          isSubmitting={isSubmitting}
          consentGiven={consentGiven}
          onCancel={handleCancel}
          onProceed={handleApplicantConsentProceed}
        />
      </Box>
    );
  }

  // ── Full OCR mode — editable form ─────────────────────────────────────────
  if (!initialValues) return <></>;

  const hasSecondHolder = Object.values(initialValues.secondHolderDetails).some(Boolean);

  return (
    <Box>
      {pageTitle}
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        validationSchema={reviewFetchedDetailsSchema}
        onSubmit={handleOcrProceed}>
        {({ values }) => (
          <Form noValidate>
            {/* ── Applicant Details ── */}
            <SectionHeading title="Applicant Details" />
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <MFTextField name="applicantDetails.panNo" label="PAN" placeholder="Enter PAN" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  name="applicantDetails.dateOfBirth"
                  label="Date of Birth"
                  inputLabelStyles={{
                    transform: 'unset',
                    fontSize: 14,
                    fontWeight: 500,
                    color: 'rgba(0,0,0,0.7)',
                  }}
                  placeholder="DD/MM/YYYY"
                  maxDate={new Date()}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MFTextField
                  name="applicantDetails.mobile"
                  label="Mobile Number"
                  placeholder="Enter mobile number"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MFTextField
                  name="applicantDetails.email"
                  label="Email ID"
                  placeholder="Enter email ID"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MFSelectField
                  name="applicantDetails.grossAnnualIncome"
                  label="Annual Income"
                  items={ANNUAL_INCOME_ITEMS}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MFTextField
                  name="applicantDetails.netWorth"
                  label="Net Worth"
                  placeholder="Enter net worth"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MFSelectField
                  name="applicantDetails.occupationDetails"
                  label="Occupation"
                  items={OCCUPATION_ITEMS}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MFTextField name="applicantDetails.dpId" label="DP ID" placeholder="Enter DP ID" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MFTextField
                  name="applicantDetails.clId"
                  label="Client ID"
                  placeholder="Enter client ID"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MFSelectField
                  name="applicantDetails.dlclId"
                  label="NSDL / CDSL"
                  items={DLCL_ITEMS}
                />
              </Grid>
            </Grid>

            {/* ── Bank Details ── */}
            <Divider sx={{ my: 2 }} />
            <SectionHeading title="Bank Details" />
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <MFTextField
                  name="bankDetails.bankAccountNumber"
                  label="Account Number"
                  placeholder="Enter account number"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MFTextField
                  name="bankDetails.bankName"
                  label="Bank Name"
                  placeholder="Enter bank name"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MFSelectField
                  name="bankDetails.bankAccountType"
                  label="Account Type"
                  items={ACCOUNT_TYPE_ITEMS}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MFTextField
                  name="bankDetails.ifscCode"
                  label="IFSC Code"
                  placeholder="Enter IFSC code"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MFTextField
                  name="bankDetails.micrCode"
                  label="MICR Code"
                  placeholder="Enter MICR code"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MFTextField
                  name="bankDetails.leiNumber"
                  label="LEI Number"
                  placeholder="Enter LEI number"
                />
              </Grid>
              <Grid item xs={12}>
                <MFTextField
                  name="bankDetails.address1"
                  label="Bank Address"
                  placeholder="Enter bank address"
                />
              </Grid>
            </Grid>

            {/* ── Nominee Details ── */}
            {values.nomineeDetails.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <SectionHeading title="Nominee Details" />
                {values.nomineeDetails.map((_, idx) => (
                  <Grid container spacing={1.5} key={idx}>
                    <Grid item xs={12} sm={6}>
                      <MFTextField
                        name={`nomineeDetails.${idx}.nomineeName`}
                        label="Nominee Name"
                        placeholder="Enter nominee name"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MFTextField
                        name={`nomineeDetails.${idx}.nomineePan`}
                        label="Nominee PAN"
                        placeholder="Enter nominee PAN"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MFTextField
                        name={`nomineeDetails.${idx}.nomineePercentage`}
                        label="Nominee Percentage %"
                        placeholder="Enter percentage"
                      />
                    </Grid>
                  </Grid>
                ))}
              </>
            )}

            {/* ── Second Holder Details ── */}
            {hasSecondHolder && (
              <>
                <Divider sx={{ my: 2 }} />
                <SectionHeading title="Second Holder Details" />
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <MFTextField
                      name="secondHolderDetails.panNo"
                      label="PAN"
                      placeholder="Enter PAN"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      name="secondHolderDetails.dateOfBirth"
                      label="Date of Birth"
                      placeholder="DD/MM/YYYY"
                      inputLabelStyles={{
                        transform: 'unset',
                        fontSize: 14,
                        fontWeight: 500,
                        color: 'rgba(0,0,0,0.7)',
                      }}
                      maxDate={new Date()}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <MFTextField
                      name="secondHolderDetails.mobile"
                      label="Mobile Number"
                      placeholder="Enter mobile number"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <MFTextField
                      name="secondHolderDetails.email"
                      label="Email ID"
                      placeholder="Enter email ID"
                    />
                  </Grid>
                </Grid>
              </>
            )}

            <ConsentCheckbox checked={consentGiven} onChange={setConsentGiven} />
            <ActionButtons
              isSubmitting={isSubmitting}
              consentGiven={consentGiven}
              onCancel={handleCancel}
            />
          </Form>
        )}
      </Formik>
    </Box>
  );
}
